"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateFieldBase = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_day_picker_1 = require("react-day-picker");
const react_stately_1 = require("react-stately");
const components_1 = require("../../components");
const internal_1 = require("../../components/internal");
const DatePickerOverlay_1 = require("../../components/internal/DatePicker/DatePickerOverlay");
const Css_1 = require("../../Css");
const utils_1 = require("./utils");
const TextFieldBase_1 = require("../TextFieldBase");
const utils_2 = require("../../utils");
const defaultTestId_1 = require("../../utils/defaultTestId");
function DateFieldBase(props) {
    var _a;
    const { label, disabled, required, value, onFocus, onBlur, 
    // Pull `onChange` out of the props, but we're not directly using it. Do not want to keep it in `...others`
    onChange: _onChange, errorMsg, helperText, readOnly, format = "short", iconLeft = false, hideCalendarIcon = false, disabledDays, onEnter, defaultOpen, mode, isRangeFilterField = false, ...others } = props;
    const isRangeMode = mode === "range";
    const inputRef = (0, react_1.useRef)(null);
    const inputWrapRef = (0, react_1.useRef)(null);
    const buttonRef = (0, react_1.useRef)(null);
    const overlayRef = (0, react_1.useRef)(null);
    // Local focus state to conditionally call onBlur when the date picker closes.
    // E.g. If the picker closes due to focus going back to the input field then don't call onBlur. Also used to avoid updating WIP values
    const [isFocused, setIsFocused] = (0, react_1.useState)(false);
    const dateFormat = (0, utils_1.getDateFormat)(format);
    // The `wipValue` allows the "range" mode to set the value to `undefined`, even if the `onChange` response cannot be undefined.
    // This makes working within the DateRangePicker much more user friendly.
    const [wipValue, setWipValue] = (0, react_1.useState)(value);
    const [inputValue, setInputValue] = (0, react_1.useState)((_a = (isRangeMode ? (0, utils_1.formatDateRange)(props.value, dateFormat) : (0, utils_1.formatDate)(props.value, dateFormat))) !== null && _a !== void 0 ? _a : "");
    const tid = (0, utils_2.useTestIds)(props, (0, defaultTestId_1.defaultTestId)(label));
    const isDisabled = !!disabled;
    const isReadOnly = !!readOnly;
    const textFieldProps = {
        ...others,
        label,
        isDisabled,
        isReadOnly,
        "aria-haspopup": "dialog",
        value: inputValue,
    };
    const state = (0, react_stately_1.useOverlayTriggerState)({
        onOpenChange: (isOpen) => {
            // Handles calling `onBlur` for the case where the user interacts with the overlay, removing focus from the input field, and eventually closes the overlay (whether clicking away, or selecting a date)
            if (!isOpen && !isFocused) {
                (0, utils_2.maybeCall)(onBlur);
            }
        },
        isOpen: defaultOpen,
    });
    const { labelProps, inputProps } = (0, react_aria_1.useTextField)({
        ...textFieldProps,
        onFocus: () => {
            var _a;
            // Open overlay on focus of the input.
            state.open();
            setIsFocused(true);
            (0, utils_2.maybeCall)(onFocus);
            if (wipValue && dateFormat !== utils_1.dateFormats.short) {
                // When focused, change to use the "short" date format, as it is simpler to update by hand and parse.
                setInputValue((_a = (isRangeMode
                    ? (0, utils_1.formatDateRange)(props.value, utils_1.dateFormats.short)
                    : (0, utils_1.formatDate)(props.value, utils_1.dateFormats.short))) !== null && _a !== void 0 ? _a : "");
            }
        },
        onBlur: (e) => {
            var _a, _b;
            setIsFocused(false);
            // If we are interacting any other part of `inputWrap` ref (such as the calendar button) return early as clicking anywhere within there will push focus to the input field.
            // Or if interacting with the DatePicker then also return early. The overlay will handle calling `onBlur` once it closes.
            if ((inputWrapRef.current && inputWrapRef.current.contains(e.relatedTarget)) ||
                (overlayRef.current && overlayRef.current.contains(e.relatedTarget))) {
                return;
            }
            const parsedDate = isRangeMode
                ? (0, utils_1.parseDateRange)(inputValue, utils_1.dateFormats.short)
                : (0, utils_1.parseDate)(inputValue, utils_1.dateFormats.short);
            // If the user leaves the input and has an invalid date, reset to previous value.
            if (!isParsedDateValid(parsedDate)) {
                setWipValue(value);
                setInputValue((_a = (isRangeMode ? (0, utils_1.formatDateRange)(props.value, dateFormat) : (0, utils_1.formatDate)(props.value, dateFormat))) !== null && _a !== void 0 ? _a : "");
            }
            else if (dateFormat !== utils_1.dateFormats.short) {
                // Or if we need to reset the dateFormat back from `short` to whatever the user specified
                setInputValue((_b = (isRangeMode ? (0, utils_1.formatDateRange)(props.value, dateFormat) : (0, utils_1.formatDate)(props.value, dateFormat))) !== null && _b !== void 0 ? _b : "");
            }
            state.close();
            (0, utils_2.maybeCall)(onBlur);
        },
        onKeyDown: (e) => {
            var _a;
            if (e.key === "Enter") {
                (0, utils_2.maybeCall)(onEnter);
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            }
        },
    }, inputRef);
    const { triggerProps, overlayProps } = (0, react_aria_1.useOverlayTrigger)({ type: "dialog" }, state, buttonRef);
    const { buttonProps } = (0, react_aria_1.useButton)({
        ...triggerProps,
        isDisabled: isDisabled || isReadOnly,
        // When pressed or focused then move focus the input, which will select the text and trigger the DatePicker to open
        onPress: () => { var _a; return (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        onFocus: () => { var _a; return (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }, buttonRef);
    const { overlayProps: positionProps } = (0, react_aria_1.useOverlayPosition)({
        targetRef: inputWrapRef,
        overlayRef,
        shouldFlip: true,
        isOpen: state.isOpen,
        onClose: state.close,
        placement: "bottom left",
        shouldUpdatePosition: true,
        offset: 4,
    });
    // Handle case where the input value is updated from outside the component.
    (0, react_1.useEffect)(() => {
        var _a;
        // Avoid updating any WIP values.
        if (!isFocused && !state.isOpen) {
            setWipValue(value);
            setInputValue((_a = (isRangeMode ? (0, utils_1.formatDateRange)(props.value, dateFormat) : (0, utils_1.formatDate)(props.value, dateFormat))) !== null && _a !== void 0 ? _a : "");
        }
        // We don't want to update the internal `wipValue` or `inputValue` back to `value` just because focus state changes or the overlay opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, dateFormat]);
    // Create a type safe `onChange` to handle both Single and Range date fields.
    const onChange = (0, react_1.useCallback)((d) => {
        setWipValue(d);
        if (d && isParsedDateValid(d)) {
            if (isRangeMode && (0, react_day_picker_1.isDateRange)(d)) {
                props.onChange(d);
                return;
            }
            if (!isRangeMode && !(0, react_day_picker_1.isDateRange)(d)) {
                props.onChange(d);
                return;
            }
        }
        else {
            props.onChange(undefined);
            return;
        }
    }, [isRangeMode, props.onChange]);
    // If showing the short date format, "01/01/20", so set size to 8. If medium (Wed, Nov 23) use 10 characters (leaving out the `,` character in the count because it is so small)
    // Otherwise the long format can be `undefined`.
    // Setting the size attribute only impacts the fields when displayed in a container that doesn't allow the field to grow to its max width, such as in an inline container.
    // TODO: figure this out... seems weird to have now that we support multiple dates formats....
    // How do other applications handle this defined sizing? Appears they use hard coded widths depending on format, which is similar here (using `size` instead of css `width`).
    // But would also need to allow for the input to be `fullWidth`, which is basically also what we're accomplishing here... so maybe fine?
    const inputSize = !isRangeMode ? (format === "short" ? 8 : format === "medium" ? 10 : undefined) : undefined;
    // Support input range filter field w/ a clear btn that will appear when overlay is closed and input is not focused
    const clearButton = ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: inputValue !== "" && !state.isOpen && ((0, jsx_runtime_1.jsx)(components_1.IconButton, { icon: "xCircle", color: Css_1.Palette.Gray700, onClick: () => {
                setInputValue("");
                onChange(undefined);
            } })) }));
    const calendarButton = ((0, jsx_runtime_1.jsx)("button", { ref: buttonRef, ...buttonProps, disabled: isDisabled, css: Css_1.Css.if(isDisabled).cursorNotAllowed.$, tabIndex: -1, ...tid.calendarButton, children: (0, jsx_runtime_1.jsx)(components_1.Icon, { icon: "calendar", color: isDisabled ? Css_1.Palette.Gray400 : Css_1.Palette.Gray700 }) }));
    const EndFieldButtons = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [isRangeFilterField && clearButton, !hideCalendarIcon && calendarButton] }));
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(TextFieldBase_1.TextFieldBase, { ...textFieldProps, errorMsg: errorMsg, helperText: helperText, required: required, labelProps: labelProps, inputProps: { ...inputProps, size: inputSize }, inputRef: inputRef, inputWrapRef: inputWrapRef, onChange: (v) => {
                    // hide the calendar if the user is manually entering the date
                    state.close();
                    if (v) {
                        setInputValue(v);
                        // If changing the value directly (vs using the DatePicker), then we always use the short format
                        const parsed = isRangeMode ? (0, utils_1.parseDateRange)(v, utils_1.dateFormats.short) : (0, utils_1.parseDate)(v, utils_1.dateFormats.short);
                        onChange(parsed);
                    }
                    // User has deleted all text in field
                    else if (v === undefined) {
                        setInputValue("");
                    }
                }, endAdornment: !iconLeft && EndFieldButtons, startAdornment: !hideCalendarIcon && iconLeft && calendarButton, tooltip: (0, components_1.resolveTooltip)(disabled, undefined, readOnly), ...others }), state.isOpen && ((0, jsx_runtime_1.jsx)(internal_1.Popover, { triggerRef: inputWrapRef, popoverRef: overlayRef, positionProps: positionProps, onClose: state.close, isOpen: state.isOpen, children: (0, jsx_runtime_1.jsx)(DatePickerOverlay_1.DatePickerOverlay, { overlayProps: overlayProps, children: isRangeMode ? ((0, jsx_runtime_1.jsx)(internal_1.DateRangePicker, { range: wipValue, disabledDays: disabledDays, onSelect: (dr) => {
                            var _a;
                            // Note: Do not close date range picker on select to allow the user to select multiple dates at a time
                            setInputValue((_a = (0, utils_1.formatDateRange)(dr, utils_1.dateFormats.short)) !== null && _a !== void 0 ? _a : "");
                            onChange(dr);
                        }, useYearPicker: isRangeFilterField, ...tid.datePicker })) : ((0, jsx_runtime_1.jsx)(internal_1.DatePicker, { value: wipValue, disabledDays: disabledDays, onSelect: (d) => {
                            var _a;
                            setInputValue((_a = (0, utils_1.formatDate)(d, utils_1.dateFormats.short)) !== null && _a !== void 0 ? _a : "");
                            onChange(d);
                            state.close();
                        }, ...tid.datePicker })) }) }))] }));
}
exports.DateFieldBase = DateFieldBase;
function isParsedDateValid(d) {
    // Only consider a DateRange valid when both `from` and `to` values are valid dates
    return d !== undefined && (!(0, react_day_picker_1.isDateRange)(d) || ((0, react_day_picker_1.isDateRange)(d) && (0, utils_1.isValidDate)(d.from) && (0, utils_1.isValidDate)(d.to)));
}
